import { useEffect, useState } from 'react'
import {
  Box, Typography, IconButton, Tooltip, Divider,
  Chip, TextField, Dialog, DialogTitle, DialogContent,
} from '@mui/material'
import CfpSelect from '../../comps/CfpSelect'
import { FancyButton } from '../../comps/FancyButtons'
import { DataTable, type DataTableColumn, type DataTableSort } from '../../comps/DataTable'
import AddIcon from '@mui/icons-material/Add'
import RefreshIcon from '@mui/icons-material/Refresh'
import SaveIcon from '@mui/icons-material/Save'
import EditIcon from '@mui/icons-material/Edit'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import LayersOutlinedIcon from '@mui/icons-material/LayersOutlined'
import { useApp } from '../../AppContext'
import { addToast } from '../../services/Toasts'
import { FS_SM, FS_XS, FS_XL, ICON_MD, ICON_SM, COLOR_PRIMARY_HOVER2 } from '../../common/theme'
import ResizableDivider from '../../comps/ResizableDivider'
import { saveElement, deleteElement } from '../../services/TemplatesService'
import { pmInit } from '../../services/ProjectManagerService'
import type { Project } from '../../types'
import InputModal from '../modals/InputModal'
import ConfirmModal from '../modals/ConfirmModal'
import type { TaScoringTabManageProps } from '../../types'

const noUnderline = {
  '&:before': { borderBottom: 'none' },
  '&:after': { borderBottom: 'none' },
  '&:hover:not(.Mui-disabled):before': { borderBottom: 'none' },
}

function makeId() { return crypto.randomUUID() }

const SCOPE_ALL = '__all__'
const SCOPE_GLOBAL = '__global__'

export default function ManageScoringTab({ templates, onRefresh, projectsha }: TaScoringTabManageProps) {
  const { projects: ctxProjects } = useApp()

  // Lazy-load the full project list when the AppContext doesn't have one
  // (e.g. user navigated straight to /app/templates without visiting Bid
  // Manager first). Used to resolve the per-row Scope chip's project name.
  const [pickerProjects, setPickerProjects] = useState<Project[]>(ctxProjects)
  useEffect(() => {
    if (ctxProjects.length > 0) {
      setPickerProjects(ctxProjects)
      return
    }
    let cancelled = false
    pmInit(0, 'name', true, 500, '').then(result => {
      if (cancelled) return
      const list: Project[] = result?.projects ?? []
      setPickerProjects(list)
    }).catch(() => { /* ignore - picker just falls back to project shas */ })
    return () => { cancelled = true }
  }, [ctxProjects])
  const projects = pickerProjects

  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [sort, setSort] = useState<DataTableSort>({ colId: 'name', dir: 'asc' })
  // Bump to force a re-render after mutating a template's categories or
  // checkpoints in place. Calling onRefresh() for these would refetch
  // /api2/sminit and overwrite the just-edited template.
  const [, bumpLocal] = useState(0)
  const tick = () => bumpLocal(n => n + 1)
  const [addOpen, setAddOpen] = useState(false)
  const [renameOpen, setRenameOpen] = useState(false)
  const [deleteTemplateOpen, setDeleteTemplateOpen] = useState(false)
  const [deleteCategoryTarget, setDeleteCategoryTarget] = useState<any>(null)
  const [deleteCheckpointTarget, setDeleteCheckpointTarget] = useState<{ catId: string; index: number } | null>(null)
  const [lhsWidth, setLhsWidth] = useState(320)
  // Templates tab only - scope filter for the LHS list. Defaults to All.
  const [scope, setScope] = useState<string>(SCOPE_ALL)

  // Scope picker options: "Global" plus only projects that already have at
  // least one scoring template attached.
  const scopeOptions = (() => {
    const shaSet = new Set<string>()
    for (const t of templates) if (t.projectsha) shaSet.add(t.projectsha)
    const projectOpts = Array.from(shaSet).map(sha => {
      const proj = projects.find(p => p.projectsha === sha)
      return { value: sha, label: proj?.name ?? `Project ${sha.slice(0, 8)}` }
    }).sort((a, b) => a.label.localeCompare(b.label))
    return [
      { value: SCOPE_ALL, label: 'All' },
      { value: SCOPE_GLOBAL, label: 'Global' },
      ...projectOpts,
    ]
  })()

  // Filtering: bid manager locks to its project; Templates tab uses the
  // current scope (All = everything; Global = no projectsha; else match
  // the projectsha).
  const visible = projectsha
    ? templates.filter(t => t.projectsha === projectsha)
    : (scope === SCOPE_ALL
      ? templates
      : scope === SCOPE_GLOBAL
        ? templates.filter(t => !t.projectsha)
        : templates.filter(t => t.projectsha === scope))

  const selectedTemplate = visible.find((s) => s.id === selectedId) ?? null

  const projectNameFor = (t: any) => {
    if (!t.projectsha) return ''
    return projects.find(p => p.projectsha === t.projectsha)?.name ?? ''
  }

  const sorted = [...visible].sort((a, b) => {
    let valA: string
    let valB: string
    if (sort.colId === 'scope') {
      valA = a.projectsha ? 'Project' : 'Global'
      valB = b.projectsha ? 'Project' : 'Global'
    } else if (sort.colId === 'project') {
      valA = projectNameFor(a)
      valB = projectNameFor(b)
    } else {
      valA = String(a.name ?? '')
      valB = String(b.name ?? '')
    }
    const cmp = valA.localeCompare(valB)
    return sort.dir === 'asc' ? cmp : -cmp
  })

  const scoringCols: DataTableColumn<any>[] = [
    {
      id: 'name',
      label: 'Name',
      width: 140,
      flex: 1,
      minWidth: 80,
      render: (t) => (
        <Typography variant="caption" sx={{
          fontSize: FS_SM,
          fontWeight: t.id === selectedId ? 600 : 400,
          color: t.id === selectedId ? 'primary.main' : 'text.primary',
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}>{t.name}</Typography>
      ),
    },
    {
      id: 'scope',
      label: 'Scope',
      width: 70,
      render: (t) => {
        const isGlobal = !t.projectsha
        return (
          <Chip
            size="small"
            label={isGlobal ? 'Global' : 'Project'}
            sx={{
              height: 16, fontSize: '0.6rem', maxWidth: '100%',
              bgcolor: isGlobal ? 'action.selected' : 'primary.main',
              color: isGlobal ? 'text.secondary' : 'primary.contrastText',
              '& .MuiChip-label': { px: 0.6, overflow: 'hidden', textOverflow: 'ellipsis' },
            }}
          />
        )
      },
    },
    // Project name column - shown in the Templates tab unless the scope is
    // Global (every row would be empty). Hidden in bid manager (locked to a
    // single project, redundant).
    ...(projectsha || scope === SCOPE_GLOBAL ? [] : [{
      id: 'project',
      label: 'Project',
      width: 120,
      flex: 1,
      minWidth: 60,
      render: (t: any) => (
        <Typography variant="caption" sx={{
          fontSize: FS_XS, opacity: t.projectsha ? 1 : 0.45,
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}>{projectNameFor(t)}</Typography>
      ),
    }]),
  ]

  // All scoring-template mutations go through two backend endpoints:
  //   POST /api2/besaveelement  - create / rename / save (the whole group)
  //   POST /api2/bedelelement   - delete (the whole group)
  // The backend distinguishes create vs update by whether the group has an id.

  const handleAddTemplate = async (name: string, targetProjectSha?: string) => {
    if (!name.trim()) return
    try {
      const sha = projectsha ?? targetProjectSha
      const body: Record<string, any> = {
        name: name.trim(),
        type: 'score',
        categories: [],
      }
      if (sha) body.projectsha = sha
      await saveElement(body)
      addToast('success', 'Scoring template added.')
      onRefresh?.()
    } catch {
      addToast('error', 'Failed to add scoring template.')
    }
  }

  const handleRenameTemplate = async (newname: string) => {
    if (!selectedTemplate || !newname.trim()) return
    try {
      selectedTemplate.name = newname.trim()
      await saveElement(selectedTemplate)
      addToast('success', 'Scoring template renamed.')
      onRefresh?.()
    } catch {
      addToast('error', 'Failed to rename scoring template.')
    }
  }

  const handleDeleteTemplate = async () => {
    if (!selectedTemplate) return
    try {
      await deleteElement(selectedTemplate)
      addToast('success', 'Scoring template deleted.')
      setSelectedId(null)
      onRefresh?.()
    } catch {
      addToast('error', 'Failed to delete scoring template.')
    }
  }

  const handleSaveTemplate = async () => {
    if (!selectedTemplate) return
    try {
      await saveElement(selectedTemplate)
      addToast('success', 'Scoring template saved.')
      onRefresh?.()
    } catch {
      addToast('error', 'Failed to save scoring template.')
    }
  }

  const handleAddCategory = () => {
    if (!selectedTemplate) return
    selectedTemplate.categories = [
      ...(selectedTemplate.categories ?? []),
      { id: makeId(), category: '', checkpoints: [] },
    ]
    tick()
  }

  const handleDeleteCategory = () => {
    if (!deleteCategoryTarget || !selectedTemplate) return
    selectedTemplate.categories = (selectedTemplate.categories ?? []).filter(
      (c: any) => c.id !== deleteCategoryTarget.id
    )
    setDeleteCategoryTarget(null)
    tick()
  }

  const handleAddCheckpoint = (cat: any) => {
    cat.checkpoints = [...(cat.checkpoints ?? []), '']
    tick()
  }

  const handleDeleteCheckpoint = () => {
    if (!deleteCheckpointTarget || !selectedTemplate) return
    const cat = (selectedTemplate.categories ?? []).find((c: any) => c.id === deleteCheckpointTarget.catId)
    if (cat) cat.checkpoints = (cat.checkpoints ?? []).filter((_: string, i: number) => i !== deleteCheckpointTarget.index)
    setDeleteCheckpointTarget(null)
    tick()
  }

  const handleCheckpointChange = (cat: any, index: number, value: string) => {
    if (!cat.checkpoints) return
    cat.checkpoints[index] = value
  }

  return (
    <Box data-testid="bms_root" sx={{ display: 'flex', flexDirection: 'column', overflow: 'hidden', minHeight: 0, flex: 1 }}>

      {/* Modals */}
      {projectsha ? (
        <InputModal
          open={addOpen}
          title="Add Scoring Template"
          okLabel="Add"
          placeholder="Template name"
          singleLine
          onConfirm={(name) => handleAddTemplate(name)}
          onClose={() => setAddOpen(false)}
        />
      ) : (
        <AddTemplateDialog
          open={addOpen}
          title="Add Scoring Template"
          projects={projects}
          defaultProjectSha={scope === SCOPE_GLOBAL ? '' : scope}
          onConfirm={(name, sha) => handleAddTemplate(name, sha || undefined)}
          onClose={() => setAddOpen(false)}
        />
      )}
      <InputModal
        open={renameOpen}
        title="Rename Scoring Template"
        okLabel="Rename"
        placeholder="New name"
        singleLine
        onConfirm={handleRenameTemplate}
        onClose={() => setRenameOpen(false)}
      />
      <ConfirmModal
        open={deleteTemplateOpen}
        title="Delete Scoring Template"
        body={`Are you sure you want to delete "${selectedTemplate?.name}"?`}
        okLabel="Delete"
        onConfirm={handleDeleteTemplate}
        onClose={() => setDeleteTemplateOpen(false)}
      />
      <ConfirmModal
        open={!!deleteCategoryTarget}
        title="Delete Category"
        body={`Are you sure you want to delete "${deleteCategoryTarget?.category}"?`}
        okLabel="Delete"
        onConfirm={handleDeleteCategory}
        onClose={() => setDeleteCategoryTarget(null)}
      />
      <ConfirmModal
        open={!!deleteCheckpointTarget}
        title="Delete Checkpoint"
        body="Are you sure you want to delete this checkpoint?"
        okLabel="Delete"
        onConfirm={handleDeleteCheckpoint}
        onClose={() => setDeleteCheckpointTarget(null)}
      />

      {/* Toolbar */}
      <Box sx={{ display: 'flex', alignItems: 'center', px: 1, py: 0.75, gap: 0.5, flexShrink: 0 }}>
        <Typography variant="caption" sx={{ fontWeight: 600, flex: 1 }}>Scoring Templates</Typography>
        <Tooltip title="Refresh">
          <IconButton data-testid="bms_refresh_btn" size="small" onClick={onRefresh}><RefreshIcon sx={{ fontSize: ICON_MD }} /></IconButton>
        </Tooltip>
        <Tooltip title="Add Template">
          <IconButton data-testid="bms_add_template_btn" size="small" onClick={() => setAddOpen(true)}><AddIcon sx={{ fontSize: ICON_MD }} /></IconButton>
        </Tooltip>
      </Box>
      <Divider />

      {/* Split panel */}
      <Box sx={{ display: 'flex', flex: 1, overflow: 'hidden', minHeight: 0 }}>

        {/* LHS list */}
        <Box sx={{ width: lhsWidth, flexShrink: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          {!projectsha && (
            <Box sx={{ display: 'flex', alignItems: 'center', px: 1, py: 0.5, borderBottom: 1, borderColor: 'divider', flexShrink: 0, gap: 0.5 }}>
              <CfpSelect
                label="Scope"
                value={scope}
                options={scopeOptions}
                onChange={setScope}
                testId="bms_scope_picker"
              />
            </Box>
          )}
          <DataTable
            rows={sorted}
            cols={scoringCols}
            rowKey={(s, i) => s.id ?? i}
            rowTestId="bms_template_row"
            rowDataAttrs={(s) => ({ 'data-template-id': s.id })}
            onRowClick={(s) => setSelectedId(s.id)}
            rowSx={(s) => s.id === selectedId ? { bgcolor: COLOR_PRIMARY_HOVER2 } : {}}
            sort={sort}
            onSortChange={(colId, dir) => setSort({ colId, dir })}
            testIdPrefix="bms_table"
          />
        </Box>

        {/* Panel divider - drag to resize the LHS list */}
        <ResizableDivider onDelta={(dx) => setLhsWidth(w => Math.max(140, Math.min(500, w + dx)))} />

        {/* RHS */}
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', minWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', px: 1, py: 0.75, gap: 0.5, borderBottom: 1, borderColor: 'divider', flexShrink: 0 }}>
            {selectedTemplate ? (
              <Chip size="small" icon={<LayersOutlinedIcon />} label={selectedTemplate.name} sx={{ mr: 'auto', fontSize: FS_XS }} />
            ) : (
              <Box sx={{ flex: 1 }} />
            )}
            <Tooltip title="Save Template">
              <span>
                <IconButton data-testid="bms_save_template_btn" size="small" disabled={!selectedTemplate} onClick={handleSaveTemplate}><SaveIcon sx={{ fontSize: ICON_MD }} /></IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Rename Template">
              <span>
                <IconButton data-testid="bms_rename_template_btn" size="small" disabled={!selectedTemplate} onClick={() => setRenameOpen(true)}><EditIcon sx={{ fontSize: ICON_MD }} /></IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Delete Template">
              <span>
                <IconButton data-testid="bms_delete_template_btn" size="small" disabled={!selectedTemplate} onClick={() => setDeleteTemplateOpen(true)}><DeleteOutlineIcon sx={{ fontSize: ICON_MD }} /></IconButton>
              </span>
            </Tooltip>
          </Box>

          {selectedTemplate ? (
            <Box sx={{ flex: 1, overflowY: 'auto', p: 1 }}>

              {/* Categories */}
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                <Typography variant="caption" sx={{ fontWeight: 600, flex: 1 }}>Categories</Typography>
                <Tooltip title="Add Category">
                  <IconButton size="small" onClick={handleAddCategory}><AddIcon sx={{ fontSize: ICON_SM }} /></IconButton>
                </Tooltip>
              </Box>
              <Divider sx={{ mb: 1 }} />

              {(selectedTemplate.categories ?? []).length === 0 && (
                <Typography variant="caption" sx={{ opacity: 0.45, display: 'block', px: 0.5, mb: 1 }}>
                  No categories. Click + to add one.
                </Typography>
              )}

              {(selectedTemplate.categories ?? []).map((cat: any) => (
                <Box key={cat.id} sx={{ border: 1, borderColor: 'divider', borderRadius: 1, mb: 1, overflow: 'hidden' }}>

                  {/* Category header */}
                  <Box sx={{ display: 'flex', alignItems: 'center', px: 1, py: 0.5, bgcolor: 'action.hover', gap: 0.5 }}>
                    <TextField
                      size="small"
                      placeholder="Category name"
                      defaultValue={cat.category ?? ''}
                      onChange={e => { cat.category = e.target.value }}
                      variant="standard"
                      sx={{ flex: 1, fontSize: '0.8rem', '& input': { fontSize: '0.8rem', py: 0.25 }, ...noUnderline }}
                    />
                    <Tooltip title="Add Checkpoint">
                      <IconButton size="small" onClick={() => handleAddCheckpoint(cat)} sx={{ p: 0.25 }}>
                        <AddIcon sx={{ fontSize: FS_XL }} />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete Category">
                      <IconButton size="small" onClick={() => setDeleteCategoryTarget(cat)} sx={{ p: 0.25, opacity: 0.55, '&:hover': { opacity: 1 } }}>
                        <DeleteOutlineIcon sx={{ fontSize: FS_XL }} />
                      </IconButton>
                    </Tooltip>
                  </Box>

                  {/* Checkpoint rows */}
                  <Box sx={{ p: 0.75 }}>
                    {(cat.checkpoints ?? []).length === 0 && (
                      <Typography variant="caption" sx={{ opacity: 0.4, display: 'block', px: 0.5 }}>No checkpoints</Typography>
                    )}
                    {(cat.checkpoints ?? []).map((cp: string, idx: number) => (
                      <Box key={idx} sx={{ display: 'flex', alignItems: 'center', gap: 0.75, py: 0.25 }}>
                        <TextField
                          size="small"
                          placeholder="Checkpoint"
                          defaultValue={cp ?? ''}
                          onChange={e => handleCheckpointChange(cat, idx, e.target.value)}
                          variant="outlined"
                          sx={{ flex: 1, '& input': { fontSize: FS_SM, py: 0.4 } }}
                        />
                        <Tooltip title="Delete">
                          <IconButton size="small" onClick={() => setDeleteCheckpointTarget({ catId: cat.id, index: idx })} sx={{ p: 0.25, opacity: 0.5, '&:hover': { opacity: 1 } }}>
                            <DeleteOutlineIcon sx={{ fontSize: FS_XL }} />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    ))}
                  </Box>
                </Box>
              ))}
            </Box>
          ) : (
            <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Typography variant="caption" sx={{ opacity: 0.4 }}>Select a template</Typography>
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  )
}

// Add dialog used in Templates tab - captures both a name and a target
// scope (Global or a specific project). The bid manager view uses the
// simpler InputModal because the projectsha is locked.
export function AddTemplateDialog({
  open, title, projects, defaultProjectSha = '', onConfirm, onClose,
}: {
  open: boolean
  title: string
  projects: Project[]
  defaultProjectSha?: string
  onConfirm: (name: string, projectsha: string) => void
  onClose: () => void
}) {
  const [name, setName] = useState('')
  const [projectShaSel, setProjectShaSel] = useState(defaultProjectSha)

  useEffect(() => {
    if (open) { setName(''); setProjectShaSel(defaultProjectSha) }
  }, [open, defaultProjectSha])

  const submit = () => {
    const trimmed = name.trim()
    if (!trimmed) return
    onConfirm(trimmed, projectShaSel)
    onClose()
  }

  const projectOptions = [
    { value: '', label: 'Global' },
    ...[...projects]
      .sort((a, b) => String(a.name ?? '').localeCompare(String(b.name ?? '')))
      .map(p => ({ value: p.projectsha, label: p.name })),
  ]

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ px: 2.5, pt: 2, pb: 1.5 }}>
        <Typography component="span" variant="subtitle1" sx={{ fontWeight: 600, fontSize: '0.95rem' }}>
          {title}
        </Typography>
      </DialogTitle>
      <Divider />
      <DialogContent sx={{ px: 2.5, pt: 2, pb: 2 }}>
        <form onSubmit={(e) => { e.preventDefault(); submit() }}>
          <Typography variant="caption" sx={{ fontWeight: 500, opacity: 0.7, display: 'block', mb: 0.5 }}>Name</Typography>
          <TextField
            autoFocus fullWidth size="small"
            placeholder="Template name"
            value={name}
            onChange={e => setName(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); submit() } }}
            sx={{ mb: 2 }}
          />
          <Typography variant="caption" sx={{ fontWeight: 500, opacity: 0.7, display: 'block', mb: 0.5 }}>Scope</Typography>
          <Box sx={{ mb: 2 }}>
            <CfpSelect
              value={projectShaSel}
              onChange={setProjectShaSel}
              options={projectOptions}
              fullWidth
            />
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
            <FancyButton type="button" onClick={onClose}>Cancel</FancyButton>
            <FancyButton type="submit" solid disabled={!name.trim()}>Add</FancyButton>
          </Box>
        </form>
      </DialogContent>
    </Dialog>
  )
}

